import React, { useState, useMemo } from 'react';
import { 
  Cake, Calendar, Gift, Mail, Phone, Heart, 
  TrendingUp, Users, PartyPopper 
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { format, parse, differenceInYears, isSameDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getInitials, getAvatarColor } from '../../utils/helpers';
import Button from '../common/Button';
import Modal from '../common/Modal';
import { logger, performance, logFirebaseError } from '../../utils/logger';


const BirthdaysView = () => {
  const { data } = useData();
  const { currentUser } = useAuth();
  const { createNotification } = useNotifications();
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [filterMonth, setFilterMonth] = useState('all');

  // Calculer les anniversaires
  const birthdays = useMemo(() => {
    if (!data.members) return [];

    const today = new Date();
    
    return data.members
      .filter(member => member.dateOfBirth)
      .map(member => {
        try {
          const birthDate = parse(member.dateOfBirth, 'yyyy-MM-dd', new Date());
          const age = differenceInYears(today, birthDate);
          
          // Prochaine date d'anniversaire cette année
          const nextBirthday = new Date(
            today.getFullYear(),
            birthDate.getMonth(),
            birthDate.getDate()
          );
          
          // Si déjà passé cette année, prendre l'année prochaine
          if (nextBirthday < today) {
            nextBirthday.setFullYear(today.getFullYear() + 1);
          }

          // Jours restants
          const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));
          const isToday = isSameDay(birthDate, today);
          const isThisWeek = daysUntil <= 7 && daysUntil >= 0;

          return {
            ...member,
            birthDate,
            age,
            nextBirthday,
            daysUntil,
            isToday,
            isThisWeek,
            month: birthDate.getMonth()
          };
        } catch (error) {
          logger.error('Erreur parsing date:', member.dateOfBirth);
          return null;
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.daysUntil - b.daysUntil);
  }, [data.members]);

  // Filtrer par mois
  const filteredBirthdays = useMemo(() => {
    if (filterMonth === 'all') return birthdays;
    return birthdays.filter(b => b.month === parseInt(filterMonth));
  }, [birthdays, filterMonth]);

  // Statistiques
  const stats = {
    today: birthdays.filter(b => b.isToday).length,
    thisWeek: birthdays.filter(b => b.isThisWeek).length,
    thisMonth: birthdays.filter(b => b.month === new Date().getMonth()).length,
    total: birthdays.length
  };

  // Envoyer une carte de vœux
  const sendBirthdayCard = async (member) => {
    try {
      await createNotification({
        type: 'announcement',
        title: '🎂 Joyeux anniversaire !',
        message: `${currentUser.name} vous souhaite un joyeux anniversaire ! 🎉`,
        icon: 'Cake',
        link: 'dashboard',
        targetUsers: [member.userId || member.id],
        createdBy: currentUser.id
      });

      alert(`Carte d'anniversaire envoyée à ${member.name} !`);
      setShowCardModal(false);
    } catch (error) {
      logger.error('Erreur envoi carte:', error);
      alert('Erreur lors de l\'envoi');
    }
  };

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Cake size={32} className="text-pink-500" />
            Anniversaires
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Ne manquez aucun anniversaire de la communauté
          </p>
        </div>

        {/* Filtre par mois */}
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">Tous les mois</option>
          {months.map((month, idx) => (
            <option key={idx} value={idx}>{month}</option>
          ))}
        </select>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-pink-50 to-red-50 dark:from-pink-900/20 dark:to-red-900/20 p-4 rounded-lg border-2 border-pink-200 dark:border-pink-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pink-600 dark:text-pink-400 font-medium">Aujourd'hui</p>
              <p className="text-3xl font-bold text-pink-700 dark:text-pink-300">{stats.today}</p>
            </div>
            <Cake className="text-pink-500" size={40} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Cette semaine</p>
              <p className="text-3xl font-bold text-purple-700 dark:text-purple-300">{stats.thisWeek}</p>
            </div>
            <Calendar className="text-purple-500" size={40} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Ce mois</p>
              <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.thisMonth}</p>
            </div>
            <PartyPopper className="text-blue-500" size={40} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Total</p>
              <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">{stats.total}</p>
            </div>
            <Users className="text-amber-500" size={40} />
          </div>
        </div>
      </div>

      {/* Anniversaires du jour */}
      {stats.today > 0 && (
        <div className="bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 p-6 rounded-lg border-2 border-pink-300 dark:border-pink-700">
          <div className="flex items-center gap-3 mb-4">
            <PartyPopper size={32} className="text-pink-600 dark:text-pink-400" />
            <h2 className="text-2xl font-bold text-pink-800 dark:text-pink-300">
              🎉 Anniversaire{stats.today > 1 ? 's' : ''} aujourd'hui !
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {birthdays.filter(b => b.isToday).map((member, idx) => (
              <div 
                key={idx}
                className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-14 h-14 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold text-xl`}>
                    {getInitials(member.name)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-white">{member.name}</h3>
                    <p className="text-sm text-pink-600 dark:text-pink-400 font-medium">
                      🎂 {member.age} ans aujourd'hui !
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setSelectedMember(member);
                    setShowCardModal(true);
                  }}
                  className="w-full"
                  icon={Gift}
                  size="sm"
                >
                  Envoyer une carte
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste des anniversaires */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Prochains anniversaires
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredBirthdays.length === 0 ? (
            <div className="p-12 text-center">
              <Cake size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Aucun anniversaire {filterMonth !== 'all' ? 'ce mois' : 'enregistré'}
              </p>
            </div>
          ) : (
            filteredBirthdays.map((member, idx) => (
              <div 
                key={idx}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  member.isToday ? 'bg-pink-50 dark:bg-pink-900/20' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-12 h-12 ${getAvatarColor(member.name)} rounded-full flex items-center justify-center text-white font-bold`}>
                    {getInitials(member.name)}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {member.name}
                      </h3>
                      {member.isToday && (
                        <span className="px-2 py-1 bg-pink-500 text-white text-xs font-bold rounded-full animate-pulse">
                          AUJOURD'HUI 🎉
                        </span>
                      )}
                      {member.isThisWeek && !member.isToday && (
                        <span className="px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                          Cette semaine
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {format(member.birthDate, 'd MMMM', { locale: fr })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Cake size={14} />
                        {member.age} ans
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp size={14} />
                        dans {member.daysUntil} jour{member.daysUntil > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Envoyer un email"
                      >
                        <Mail size={20} />
                      </a>
                    )}
                    <button
                      onClick={() => {
                        setSelectedMember(member);
                        setShowCardModal(true);
                      }}
                      className="p-2 text-pink-600 hover:bg-pink-100 dark:hover:bg-pink-900/20 rounded-lg transition-colors"
                      title="Envoyer une carte de vœux"
                    >
                      <Heart size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal carte de vœux */}
      {showCardModal && selectedMember && (
        <Modal
          isOpen={showCardModal}
          onClose={() => {
            setShowCardModal(false);
            setSelectedMember(null);
          }}
          title="Carte de vœux"
        >
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/20 dark:to-purple-900/20 p-8 rounded-lg text-center">
              <Cake size={64} className="mx-auto text-pink-500 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Joyeux Anniversaire !
              </h3>
              <p className="text-xl text-gray-700 dark:text-gray-300 mb-4">
                {selectedMember.name}
              </p>
              <p className="text-lg text-pink-600 dark:text-pink-400 font-semibold">
                🎉 {selectedMember.age} ans 🎉
              </p>
            </div>

            <div className="text-center text-gray-600 dark:text-gray-400">
              <p>Une notification sera envoyée à {selectedMember.name}</p>
              <p className="text-sm mt-2">avec vos meilleurs vœux d'anniversaire !</p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCardModal(false);
                  setSelectedMember(null);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={() => sendBirthdayCard(selectedMember)}
                icon={Gift}
              >
                Envoyer la carte
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BirthdaysView;
